/**
 * The benches' textbook setups as gallery cards — what "guided experiments"
 * are to the chemistry lab, the presets are to the two physics benches: a
 * known arrangement to start from, with the claim the textbook makes about it.
 *
 * Not re-exported from the package index: it pulls both engines in, and a
 * surface that only lists simulations should not download them.
 */
import { mechanics, optics } from '@padcanvas/shared';
import type { SimulationListing } from './types';

export type SetupBench = 'mechanics' | 'optics';

/**
 * The chapters each bench belongs to, by canonical module id — the one place
 * that says so (the teacher chapter view, zitmus's catalogue and these cards
 * all read it). Mechanics: every arrangement is F = ma with a constraint, so
 * Newton's Laws of Motion. Optics: Light at Class 10, Ray Optics at Class 12.
 */
export const BENCH_CHAPTERS: Record<SetupBench, readonly string[]> = {
  mechanics: ['f2588f57-b83e-4f8b-ae74-37954d017baf'],
  optics: ['4c23b5ef-1cca-4d33-961c-dc8bb6ed4ab3', '1a93a6ee-473a-411a-a12f-d09b85b69131'],
};

/**
 * Those chapters by name, for a bench that asks which of them a saved setup
 * goes under — it has no catalogue loaded to look a name up in. The names are
 * the curriculum's (sciencexp-app's curriculum.generated.ts).
 */
export const BENCH_CHAPTER_OPTIONS: Record<SetupBench, readonly { id: string; label: string }[]> = {
  mechanics: [{ id: 'f2588f57-b83e-4f8b-ae74-37954d017baf', label: "Newton's Laws of Motion" }],
  optics: [
    { id: '4c23b5ef-1cca-4d33-961c-dc8bb6ed4ab3', label: 'Light — Class 10' },
    { id: '1a93a6ee-473a-411a-a12f-d09b85b69131', label: 'Ray Optics — Class 12' },
  ],
};

/**
 * `thumbBase` is where the simulations' `thumbs/` folder is served from on this
 * surface; each setup's picture is `setups/<bench>-<key>.png` in it, captured
 * by sciencexp-app/scripts/capture-setup-thumbs.mjs. Without one, a card shows
 * the bench's mark.
 */
export function benchSetupListings(bench: SetupBench, thumbBase?: string): SimulationListing[] {
  const presets: readonly { key: string; label: string; claim: string }[] =
    bench === 'mechanics' ? mechanics.PRESETS : optics.PRESETS;
  return presets.map(p => ({
    id: p.key,
    title: p.label,
    tagline: p.claim,
    description: p.claim,
    subject: 'physics',
    topic: bench === 'mechanics' ? 'Mechanics Bench' : 'Optics Bench',
    parameters: [],
    moduleIds: [...BENCH_CHAPTERS[bench]],
    assetUrl: '',
    thumbnailUrl: thumbBase ? `${thumbBase}setups/${bench}-${p.key}.png` : undefined,
    glyph: '',
    mark: bench,
    status: 'live',
  }));
}
